// packages/web/src/pages/EmailVerificationPage.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Alert, 
  CircularProgress,
  TextField,
  Box
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { AuthService } from '@matchpoint/shared';

// Styled components matching landing page
const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#fafafa',
});

const VerificationContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  padding: theme.spacing(3),
}));

const VerificationPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(5),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  maxWidth: 520,
  width: '100%',
  borderRadius: '16px',
  border: '1px solid #e5e5e5',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  backgroundColor: '#ffffff',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
}));

const LogoImage = styled('div')(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #84b817 0%, #c4d82f 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(132, 184, 23, 0.3)',
  '&::before': {
    content: '""',
    position: 'absolute',
    width: 18,
    height: 18,
    borderRadius: '50%',
    backgroundColor: '#c4d82f',
    top: 8,
    left: 8,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: '#679614',
    bottom: 8,
    right: 8,
  },
}));

const LogoText = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1a1a1a',
  fontFamily: '"Helvetica Neue", Arial, sans-serif',
  letterSpacing: '-0.03em',
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  marginBottom: theme.spacing(1),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.75rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  fontWeight: 400,
  lineHeight: 1.6,
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#c4d600',
  color: '#1a1a1a',
  fontSize: '1rem',
  fontWeight: 700,
  padding: theme.spacing(1.5, 3),
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(196, 214, 0, 0.25)',
  border: 'none',
  '&:hover': {
    backgroundColor: '#b3c200',
    boxShadow: '0 6px 16px rgba(196, 214, 0, 0.35)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: '#e5e5e5',
    color: '#999',
  },
  transition: 'all 0.2s ease',
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  fontSize: '1rem',
  fontWeight: 600,
  padding: theme.spacing(1.5, 3),
  borderRadius: '8px',
  border: '2px solid #e5e5e5',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderColor: '#1a1a1a',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fafafa',
    '& fieldset': {
      borderColor: '#e5e5e5',
    },
    '&:hover fieldset': {
      borderColor: '#c4d600',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#c4d600',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#1a1a1a',
    },
  },
}));

export const EmailVerificationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [token, setToken] = useState<string | null>(null);
  const [tokenChecked, setTokenChecked] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  const [email, setEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const authService = AuthService.getInstance();

  useEffect(() => {
    const emailToken = searchParams.get('token');
    if (emailToken) {
      setToken(emailToken);
      verifyEmailToken(emailToken);
    } else {
      setTokenChecked(true);
    }
  }, [searchParams]);

  const verifyEmailToken = async (emailToken: string) => {
    setLoading(true);
    try {
      const result = await authService.verifyEmailToken(emailToken);
      if (result.success) {
        setTokenValid(true);
        setSuccess(true);
        setError('');
        setTimeout(() => {
          navigate('/login?message=email-verified');
        }, 3000);
      } else {
        setTokenValid(false);
        setError('Invalid or expired verification link');
      }
    } catch (error: any) {
      setTokenValid(false);
      setError(error.message || 'Failed to verify email');
    } finally {
      setLoading(false);
      setTokenChecked(true);
    }
  };

  const handleResendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setResendLoading(true);
    setError('');

    try {
      const result = await authService.resendActivationEmail(email);
      if (result.success) {
        setSuccess(true);
        setError('');
      }
    } catch (error: any) {
      setError(error.message);
    } finally {
      setResendLoading(false);
    }
  };

  // Loading state
  if (token && !tokenChecked) {
    return (
      <PageContainer>
        <VerificationContainer>
          <VerificationPaper>
            <LogoContainer>
              <LogoImage />
              <LogoText>sched tennis</LogoText>
            </LogoContainer>
            <CircularProgress sx={{ mb: 3, color: '#c4d600' }} />
            <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center' }}>
              Verifying your email...
            </Typography>
          </VerificationPaper>
        </VerificationContainer>
      </PageContainer>
    );
  }

  // Success state
  if (token && tokenChecked && tokenValid) {
    return (
      <PageContainer>
        <VerificationContainer>
          <VerificationPaper>
            <LogoContainer>
              <LogoImage />
              <LogoText>sched tennis</LogoText>
            </LogoContainer>
            
            <PageTitle sx={{ color: '#84b817' }}>
              Email Verified!
            </PageTitle>
            
            <PageSubtitle>
              Your email has been successfully verified.<br />
              You can now sign in to your account.
            </PageSubtitle>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
              Redirecting to login page...
            </Typography>
            
            <PrimaryButton
              href="/login"
              fullWidth
            >
              Go to Login
            </PrimaryButton>
          </VerificationPaper>
        </VerificationContainer>
      </PageContainer>
    );
  }

  // Error state
  if (token && tokenChecked && !tokenValid) {
    return (
      <PageContainer>
        <VerificationContainer>
          <VerificationPaper>
            <LogoContainer>
              <LogoImage />
              <LogoText>sched tennis</LogoText>
            </LogoContainer>
            
            <PageTitle sx={{ color: '#e74c3c' }}>
              Verification Failed
            </PageTitle>
            
            <Alert severity="error" sx={{ width: '100%', mb: 4, borderRadius: '8px' }}>
              {error}
            </Alert>
            
            <PrimaryButton
              href="/verify-email"
              fullWidth
              sx={{ mb: 2 }}
            >
              Request New Verification Email
            </PrimaryButton>
            
            <SecondaryButton
              href="/login"
              fullWidth
            >
              Back to Login
            </SecondaryButton>
          </VerificationPaper>
        </VerificationContainer>
      </PageContainer>
    );
  }

  // Resend form
  return (
    <PageContainer>
      <VerificationContainer>
        <VerificationPaper>
          <LogoContainer>
            <LogoImage />
            <LogoText>sched tennis</LogoText>
          </LogoContainer>

          <PageTitle>Email Verification</PageTitle>
          <PageSubtitle>
            Enter your email address to receive<br />a new verification link.
          </PageSubtitle>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: '8px' }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 3, borderRadius: '8px' }}>
              Verification email sent! Check your inbox.
            </Alert>
          )}

          <Box component="form" onSubmit={handleResendEmail} sx={{ width: '100%' }}>
            <StyledTextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
            />

            <PrimaryButton
              type="submit"
              fullWidth
              disabled={resendLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {resendLoading ? 'Sending...' : 'Send Verification Email'}
            </PrimaryButton>

            <SecondaryButton
              fullWidth
              href="/login"
            >
              Back to Login
            </SecondaryButton>
          </Box>
        </VerificationPaper>
      </VerificationContainer>
    </PageContainer>
  );
};